import { motion } from "framer-motion";
import { createContext, useContext, useEffect, useMemo, useRef, useState, type ReactNode } from "react";

import * as uf from "../../../../common/unfathomable";
import { art } from "./assets";
import { MythosText, Rich } from "./RichText";
import css from "./Unfathomable.module.css";

// ---------------------------------------------------------------- tooltip
// The tip remembers the element that opened it and closes as soon as the pointer is no longer
// over that element — also when the element moves away or unmounts under a still mouse
// (a played card, a collapsed hand), or on any click, wheel or key press.
interface TipState { x: number; y: number; content: ReactNode; owner: number; el: Element }
interface TipApi { show: (t: TipState) => void; hide: (owner?: number) => void }
const TipCtx = createContext<TipApi>({ show: () => { }, hide: () => { } });
let tipOwners = 0;

export function TooltipLayer({ children }: { children: ReactNode }) {
  const [tip, setTip] = useState<TipState | null>(null);
  const cur = useRef<TipState | null>(null);
  cur.current = tip;
  const api = useMemo<TipApi>(() => ({
    show: t => setTip(t),
    hide: owner => setTip(t => (t && (owner === undefined || t.owner === owner) ? null : t)),
  }), []);
  useEffect(() => {
    const close = () => setTip(null);
    const move = (e: MouseEvent) => {
      const t = cur.current;
      if (t && (!t.el.isConnected || !(e.target instanceof Node) || !t.el.contains(e.target))) setTip(null);
    };
    // A card can slide out from under a still mouse: check that it is still hovered.
    const watchdog = setInterval(() => {
      const t = cur.current;
      if (t && (!t.el.isConnected || !t.el.matches(":hover"))) setTip(null);
    }, 400);
    window.addEventListener("pointerdown", close, true);
    window.addEventListener("wheel", close, true);
    window.addEventListener("keydown", close, true);
    window.addEventListener("mousemove", move, true);
    return () => {
      clearInterval(watchdog);
      window.removeEventListener("pointerdown", close, true);
      window.removeEventListener("wheel", close, true);
      window.removeEventListener("keydown", close, true);
      window.removeEventListener("mousemove", move, true);
    };
  }, []);
  return (
    <TipCtx.Provider value={api}>
      {children}
      {tip && (
        <div className={`${css.tooltip} ${css.frame}`}
          // Open toward the roomier side of the cursor so long tips are never cut off.
          style={{
            ...(tip.x > window.innerWidth - 360 ? { right: window.innerWidth - tip.x + 16 } : { left: tip.x + 16 }),
            ...(tip.y > window.innerHeight / 2 ? { bottom: window.innerHeight - tip.y + 16 } : { top: tip.y + 16 }),
            maxHeight: "70vh", overflowY: "auto",
          }}>
          {tip.content}
        </div>
      )}
    </TipCtx.Provider>
  );
}

export function useTooltip(content: ReactNode | null) {
  const api = useContext(TipCtx);
  const owner = useRef(0);
  if (owner.current === 0) owner.current = ++tipOwners;
  useEffect(() => () => api.hide(owner.current), [api]);
  if (!content) return {};
  return {
    onMouseMove: (e: React.MouseEvent) => api.show({ x: e.clientX, y: e.clientY, content, owner: owner.current, el: e.currentTarget }),
    onMouseLeave: () => api.hide(owner.current),
  };
}

// ---------------------------------------------------------------- card data
export type CardKind = "skill" | "mythos" | "waypoint" | "spell" | "item" | "damage" | "loyalty" | "back" | "feat";

export function cardInfo(kind: CardKind, id: string): { title: string; text: string; sub?: string; value?: number; type?: uf.SkillType; en?: string } {
  switch (kind) {
    case "skill": {
      const d = uf.skillDef(id);
      return { title: d.name, text: d.text, sub: `${uf.kSkillNameZh[d.type]} ${d.value}`, value: d.value, type: d.type, en: d.en };
    }
    case "mythos": {
      const m = uf.kMythosById[id];
      const check = m.check ? `检定 ${m.check.target}（${m.check.supporting.map(t => uf.kSkillNameZh[t]).join("+")}）` : "";
      return { title: m.name, en: m.en, text: m.text, sub: [check, m.chooser && m.chooser !== "current" ? `决策者：${chooserZh(m.chooser)}` : ""].filter(Boolean).join(" · ") };
    }
    case "waypoint": { const w = uf.kWaypointById[id]; return { title: w.name, en: w.en, text: w.text, sub: `距离 ${w.distance}` }; }
    case "spell": { const s = uf.kSpellById[id]; return { title: s.name, en: s.en, text: s.text, sub: "法术" }; }
    case "item": { const i = uf.kItemById[id]; return { title: i.name, en: i.en, text: i.text, sub: i.trait }; }
    case "damage": { const d = uf.kDamageById[id]; return { title: d.name, en: d.en, text: d.text, sub: "损伤" }; }
    case "loyalty": return { title: id === "human" ? "人类" : id === "hybrid" ? "混血" : "邪教徒", text: loyaltyText(id) };
    case "feat": { const c = uf.kCharacters[id as uf.CharacterId]; return { title: c.feat.name, en: c.en?.feat, text: c.feat.text, sub: `${c.name} 的特技` }; }
    default: return { title: "", text: "" };
  }
}

function chooserZh(c: string): string {
  if (c === "captain") return "船长";
  if (c === "keeper") return "典籍守护者";
  if (c === "current") return "当前玩家";
  return uf.kCharacters[c as uf.CharacterId]?.name ?? c;
}

function loyaltyText(t: string): string {
  if (t === "human") return [
    "你是人类——前提是手里只有人类忠诚卡。",
    "胜利：航迹推进到「抵达」时，场上航点距离合计 ≥ 12。",
    "注意：觉醒阶段（航程首次 ≥ 6）每人会再拿一张忠诚卡，身份可能就此改变。",
  ].join("\n");
  if (t === "hybrid") return [
    "你是混血叛徒（深潜者阵营）。只要持有这张牌，即使同时有人类卡也算叛徒。",
    "胜利：任一资源降到 0、同时有 6 个空间受损、或深潜者不够生成，游戏就此结束。",
    "可以在行动中公开身份，换用叛徒行动和叛变能力；公开之前在所有效果上都视为人类。",
  ].join("\n");
  return [
    "你是邪教徒（深潜者阵营，只在 4 / 6 人局出现）。",
    "胜利：深潜者阵营获胜，并且此时场上航点距离合计 ≥ 12——船要走得够远，又不能平安抵达。",
    "同时持有混血卡时按混血判定。",
  ].join("\n");
}

function imageFor(kind: CardKind, id: string): string {
  switch (kind) {
    case "skill": return art.skill(id);
    case "mythos": return art.mythos(id);
    case "waypoint": return art.waypoint(id);
    case "spell": return art.spell(id);
    case "item": return art.item(id);
    case "damage": return art.damage(id);
    case "loyalty": return art.loyalty(id);
    case "feat": return art.feat(id);
    case "back": return art.back(id);
  }
}

export function CardTip({ kind, id }: { kind: CardKind; id: string }) {
  const info = cardInfo(kind, id);
  return (
    <div>
      <div style={{ fontWeight: 700, fontFamily: "var(--display)" }}>{info.title}</div>
      {info.sub && <div style={{ fontSize: "0.8em", marginTop: 2 }}><Rich text={info.sub} /></div>}
      {kind === "mythos" ? <MythosText id={id} /> : <div style={{ marginTop: 4, whiteSpace: "pre-line" }}><Rich text={info.text} /></div>}
      {info.en && <div style={{ marginTop: 4, fontSize: "0.75em", opacity: 0.6, fontStyle: "italic" }}>原名：{info.en}</div>}
    </div>
  );
}

// ---------------------------------------------------------------- card face
export function CardFace(props: {
  kind: CardKind;
  id: string;
  artOk: boolean;
  width?: number;
  wide?: boolean;
  selected?: boolean;
  onClick?: () => void;
  layoutId?: string;
  noTip?: boolean;
  /** Backlight: green = helps the current check, red = hurts it. */
  glow?: "good" | "bad" | "pick";
  style?: React.CSSProperties;
}) {
  const { kind, id, artOk } = props;
  const tip = useTooltip(props.noTip || kind === "back" ? null : <CardTip kind={kind} id={id} />);
  const info = kind === "back" ? { title: "", text: "" } : cardInfo(kind, id);
  const [broken, setBroken] = useState(false);
  // Damage cards are printed landscape.
  const cls = [css.card, props.wide ? css.cardWide : "", kind === "damage" ? css.cardDamage : "", props.onClick ? css.cardSelectable : "", props.selected ? css.cardSelected : "", props.glow === "good" ? css.glowGood : props.glow === "bad" ? css.glowBad : props.glow === "pick" ? css.glowPick : ""].join(" ");
  return (
    <motion.div className={cls} layoutId={props.layoutId} onClick={props.onClick} {...tip}
      style={{ ...(props.width ? { width: props.width } : {}), ...props.style }}
      whileHover={props.onClick ? { y: -10, scale: 1.04 } : undefined}
      animate={{ y: props.selected ? -18 : 0 }}
      transition={{ type: "spring", stiffness: 380, damping: 26 }}>
      {kind === "back" && id === "neutral" ? (
        <div className={css.cardFallback} style={{ background: "radial-gradient(circle at 50% 45%, #2f6b5e, #0b1a1d 70%)", borderColor: "#b8925a", alignItems: "center", justifyContent: "center", color: "#ecd29a", fontSize: "1.6rem" }}>✦</div>
      ) : artOk && !broken ? (
        <img className={css.cardImg} src={imageFor(kind, id)} alt={info.title} draggable={false} onError={() => setBroken(true)} />
      ) : (
        <div className={css.cardFallback} style={info.type ? { borderColor: skillColor(info.type) } : undefined}>
          {info.value !== undefined && <div className={css.cardFallbackValue} style={{ color: skillColor(info.type!) }}>{info.value}</div>}
          <div style={{ fontWeight: 700 }}>{kind === "back" ? "背面" : info.title}</div>
          {info.sub && <div><Rich text={info.sub} /></div>}
          <div>{info.text}</div>
        </div>
      )}
    </motion.div>
  );
}

export function skillColor(t: uf.SkillType): string {
  return { influence: "#d36a2e", lore: "#6b3e91", observation: "#2f7a4b", strength: "#a32424", will: "#2e4f8f", treachery: "#3d3d3d" }[t];
}
